

import { EmailSentDTO } from "@/types/EmailsSentDTO";
import { IconClockExclamation, IconMail, IconMailOpened } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useUserLocale } from "@/hooks/useUserLocale";
import { useSoftBounceTooltip } from "@/hooks/useSoftBounceTooltip";
import { useEmailSkipReasonLabel } from "@/hooks/useEmailSkipReasonLabel";
import { useEventTimezone } from "@/providers/EventProvider";
import { safeDisplayTimeZone } from "@/lib/utils/datetime-tz";
import { isStaffNotice } from "@/lib/email/sendClass";
import { useOptionalRole } from "@/providers/RoleProvider";

interface SentMailEntryProps {
  email: EmailSentDTO;
  onPreview: (emailSentId: string) => void;
}

/**
 * The send time, in the EVENT's zone.
 *
 * <p>The `timeZone` argument is the whole point of these two helpers now.
 * Without it `toLocaleDateString` uses the machine's zone, and the soft-bounce
 * mark added one line above renders in the event's — so a deferral could read
 * as an hour BEFORE the send it followed, on a laptop travelling. The hook's
 * own docstring states the rule ("event timezone, app locale") and this file
 * was the surface that broke it.
 *
 * <p>An unusable zone falls back to the browser's rather than throwing;
 * `safeDisplayTimeZone` already screens the value, and a wrong hour beats an
 * empty cell in a list whose only job is chronology.
 */
function formatDate(
  date: Date | string | undefined,
  locale: string,
  timeZone: string | undefined,
): string {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone,
    });
  } catch {
    return String(date);
  }
}

/** @see #formatDate — same zone, same reason. */
function formatTime(
  date: Date | string | undefined,
  locale: string,
  timeZone: string | undefined,
): string {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    });
  } catch {
    return "";
  }
}

const STATUS_BADGE_COLORS: Record<string, string> = {
  SENT: "user-icon-font-2 outline-user-icon-2 user-icon-bg-2",
  SUCCESS: "user-icon-font-2 outline-user-icon-2 user-icon-bg-2",
  DELIVERED: "user-icon-font-4 outline-user-icon-4 user-icon-bg-4",
  BOUNCED: "user-icon-font-3 outline-user-icon-3 user-icon-bg-3",
  COMPLAINED: "user-icon-font-12 outline-user-icon-12 user-icon-bg-12",
  REJECTED: "user-icon-font-3 outline-user-icon-3 user-icon-bg-3",
  FAILED: "user-icon-font-12 outline-user-icon-12 user-icon-bg-12",
  DELAYED: "user-icon-font-11 outline-user-icon-11 user-icon-bg-11",
  // Amber like DELAYED, NOT red like BOUNCED: SES was refused once and will
  // retry, and a row only still carries this status when nothing better ever
  // followed. Painting it red is the defect this status exists to undo.
  SOFT_BOUNCED: "user-icon-font-11 outline-user-icon-11 user-icon-bg-11",
  PENDING: "user-icon-font-9 outline-user-icon-9 user-icon-bg-9",
  // Was missing, so a skipped send rendered with no badge style at all while
  // the sent-mails table gave it one — the two tables have never shared this
  // map, they only look like they do.
  SKIPPED: "user-icon-font-17 outline-user-icon-17 user-icon-bg-17",
  OPEN: "user-icon-font-7 outline-user-icon-7 user-icon-bg-7",
  CLICK: "user-icon-font-10 outline-user-icon-10 user-icon-bg-10",
};

export const SentMailEntry: React.FC<SentMailEntryProps> = ({ email, onPreview }) => {
  const t = useTranslations("guestDetails");
  const tStatus = useTranslations("optionsMultiSingleSelect");
  const tSent = useTranslations("pEmailSent");
  // Audit r2 B1-8: an arrival notice (STAFF_NOTICE) went to a staff contact
  // about this guest — never to the guest. Worded like the detail popup's
  // "To" line; the address only for a role that may manage the contacts
  // (OWNER/ADMIN — the backend blanks it for every other role). The drawer
  // is also mounted without a RoleProvider: no role reads as "hide".
  const staffNotice = isStaffNotice(email.sendClass);
  const roleContext = useOptionalRole();
  const mayShowContactAddress =
    roleContext !== null && (roleContext.role === "OWNER" || roleContext.role === "ADMIN");
  const locale = useUserLocale();
  const statusBadge = STATUS_BADGE_COLORS[email.emailStatus ?? ""] ?? "";
  const softBounceTooltip = useSoftBounceTooltip(email.softBounces);
  const skipReasonLabel = useEmailSkipReasonLabel();
  // The list requests `skipReason` (SentMailsList) and, until now, threw it
  // away here — the sent-mails table explained the same row on hover, this
  // badge did not. Same hook, same words.
  const skipTitle =
    email.emailStatus === "SKIPPED"
      ? skipReasonLabel(email.skipReason)
      : undefined;
  // The same zone the mark beside it uses, and the same one the sent-mails
  // table's date column uses (TCCDateTime). Three surfaces, one clock.
  const timeZone = safeDisplayTimeZone(useEventTimezone());
  const isOpened = email.emailStatus === "OPEN" || email.emailStatus === "CLICK";
  const Icon = isOpened ? IconMailOpened : IconMail;

  return (
    <li
      onClick={() => {
        if (email.emailSentId) onPreview(email.emailSentId);
      }}
      className="rounded-xl px-3 py-2.5 border flex flex-col gap-1 outline-tertiary cursor-pointer hover:surface-secondary transition-colors"
    >
      <div className="flex flex-row items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={14} className="font-secondary shrink-0" />
          <p className="body-3-medium truncate">
            {email.mailSubject || t("noSubject")}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Beside the status, never folded into it: the usual shape is a row
              that WAS delivered and got deferred on the way. */}
          {softBounceTooltip && (
            <span
              title={softBounceTooltip}
              data-testid="soft-bounce-chip"
              className="shrink-0 flex items-center user-icon-font-11"
            >
              <IconClockExclamation size={14} stroke={2} />
            </span>
          )}
          {email.emailStatus && (
            <span
              title={skipTitle}
              className={`body-4-regular shrink-0 px-2 py-0.5 border rounded-lg ${statusBadge}`}
            >
              {tStatus(email.emailStatus)}
            </span>
          )}
        </div>
      </div>
      {/* W2-5 G4: the reason as a LINE, not only as the badge's hover — a
          title is invisible on touch and in a screenshot, and on a SKIPPED
          row "why" is the whole story. Same hook, same words as the hover. */}
      {skipTitle && (
        <p
          data-testid="sent-mail-skip-reason"
          className="body-4-regular font-tertiary break-words"
        >
          {skipTitle}
        </p>
      )}
      <div className="flex items-center justify-between">
        <p className="body-4-regular font-tertiary truncate">
          {staffNotice ? (
            <span data-testid="sent-mail-to-notification-contact">
              {tSent("toNotificationContact")}
              {mayShowContactAddress && email.recipientMail ? ` (${email.recipientMail})` : ""}
            </span>
          ) : (
            email.templateName || email.recipientMail || ""
          )}
        </p>
        {email.sentAt && (
          <p className="body-4-regular font-tertiary shrink-0">
            {formatDate(email.sentAt, locale, timeZone)}{" "}
            {formatTime(email.sentAt, locale, timeZone)}
          </p>
        )}
      </div>
      {email.sendName && (
        <p className="body-4-regular font-tertiary truncate">
          {t("viaSend")}: &quot;{email.sendName}&quot;
        </p>
      )}
    </li>
  );
};
